import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { Activity, Boxes, HardDrive, Server, Users } from "lucide-react";
import { PageHeader, StatusPill } from "@ozpanel/kit";
import { HealthTerminal } from "./module/SmallOps";
import { Eyebrow, Meter, Panel, Section, Stat, StateView, formatBytes, useAuth } from "@ozpanel/ui";
import { useModules } from "../modules";
import { useAccountsApi } from "../accountsApi";

// Dashboard — node + cluster + module state on one screen. The first version was four
// coloured numbers and a row of links, which told an operator nothing they could act on.

// Shapes read from the real responses (GET /stats/node, GET /ozpanel/nodes), not guessed —
// the first version invented field names and rendered a column of dashes.
interface NodeStats {
  load1: number; load5: number; load15: number; cpu_count: number;
  // The load average is the container HOST's on an LXC node; CPU pressure is always the node's own.
  load_is_hosts?: boolean; cpu_pressure_60?: number | null; cpu_pressure_300?: number | null;
  mem_total_kb: number; mem_available_kb: number; mem_used_pct: number;
  disk_total_bytes: number; disk_used_bytes: number; disk_used_pct: number;
  uptime_seconds: number;
  present: boolean; stale: boolean; state: string; measured_at?: string;
}
// GET /juicefs/capacity — shared storage: what the volumes hold, and how much more fits. The volumes'
// own "free" is a fixed 1 PB and means nothing; the real limit is the storage node with the least free
// disk, because every node keeps a copy of every block. Absent fields mean "not measured", never zero.
interface StorageNode { node: string; up: boolean; disk_total_bytes: number; disk_available_bytes: number }
interface Capacity {
  used_bytes?: number;
  volumes: { volume: string; used_bytes?: number; error?: string }[];
  copies: number;
  available_bytes?: number;
  limited_by?: string;
  nodes: StorageNode[];
  detail: string;
}
interface ClusterNode {
  name: string; fqdn?: string; role?: string; version?: string;
  health?: string; services_healthy?: number; services_total?: number; self?: boolean;
  real_ip?: string; region?: string;
}

function uptime(sec?: number): string {
  if (!sec) return "—";
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h ${Math.floor((sec % 3600) / 60)}m`;
}

const pctTone = (p?: number) => (p === undefined ? undefined : p >= 90 ? "crit" : p >= 75 ? "warn" : undefined);

export function DashboardScreen() {
  const { who, client } = useAuth();
  const api = useModules();
  const accounts = useAccountsApi();

  const modules = useQuery({ queryKey: ["modules"], queryFn: api.list });
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: accounts.listTenants, retry: false });
  const apps = useQuery({ queryKey: ["apps"], queryFn: accounts.listApps, retry: false });
  const stats = useQuery({
    queryKey: ["stats-node"],
    queryFn: () => client.apiFetch<NodeStats>("/stats/node"),
    retry: false,
  });
  const capacity = useQuery({
    queryKey: ["storage-capacity"],
    queryFn: () => client.apiFetch<Capacity>("/juicefs/capacity"),
    retry: false,
  });
  const cluster = useQuery({
    queryKey: ["cluster-nodes"],
    queryFn: () => client.apiFetch<{ items: ClusterNode[]; total: number }>("/ozpanel/nodes"),
    retry: false,
  });

  const counts = { healthy: 0, degraded: 0, critical: 0, other: 0 };
  for (const m of modules.data?.items ?? []) {
    if (m.health === "healthy") counts.healthy++;
    else if (m.health === "degraded") counts.degraded++;
    else if (m.health === "critical") counts.critical++;
    else counts.other++;
  }
  const attention = counts.degraded + counts.critical;

  const node = stats.data;
  const nodes = cluster.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title={`Good to see you, ${(who?.email ?? "").split("@")[0]}`}
        subtitle="Everything this node and cluster are reporting right now."
      />

      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Stat
          label="Modules healthy"
          value={`${counts.healthy}/${modules.data?.total ?? 0}`}
          tone={attention > 0 ? "warn" : "ok"}
          hint={attention > 0 ? `${attention} need attention` : "All reporting healthy"}
          icon={<Boxes size={15} />}
        />
        <Stat
          label="Accounts"
          value={tenants.data?.total ?? "—"}
          hint={tenants.isError ? "unavailable" : "tenants on this platform"}
          icon={<Users size={15} />}
        />
        <Stat
          label="App backends"
          value={apps.data?.apps.length ?? "—"}
          hint={apps.data ? `this node: ${apps.data.this_node}` : "unavailable"}
          icon={<Activity size={15} />}
        />
        <Stat
          label="Cluster nodes"
          value={cluster.data?.total ?? nodes.length ?? "—"}
          hint={nodes.length > 1 ? "multi-node" : "single node"}
          icon={<Server size={15} />}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        <Section title="This node" actions={node && <StatusPill tone={node.stale ? "warn" : "ok"}>{node.stale ? "stale" : node.state}</StatusPill>}>
          <StateView
            loading={stats.isLoading}
            error={stats.isError ? stats.error : undefined}
            errorContext="node statistics"
            skeleton={<div className="h-24 animate-pulse rounded-md bg-surface2" />}
          >
            <div className="grid gap-5 sm:grid-cols-3">
              <Resource
                label="Memory"
                pct={node?.mem_used_pct}
                sub={node && `${formatBytes((node.mem_total_kb - node.mem_available_kb) * 1024)} of ${formatBytes(node.mem_total_kb * 1024)}`}
              />
              <Resource
                label="Disk"
                pct={node?.disk_used_pct}
                sub={node && `${formatBytes(node.disk_used_bytes)} of ${formatBytes(node.disk_total_bytes)}`}
              />
              <div>
                <Eyebrow>CPU pressure</Eyebrow>
                <div className="mt-1.5 text-[19px] font-[660] tracking-[-0.01em] tabular">
                  {node && node.cpu_pressure_300 != null ? `${node.cpu_pressure_300.toFixed(1)}%` : "—"}
                </div>
                {node && (
                  <div
                    className="mt-1 font-mono text-[11.5px] text-muted"
                    title="CPU pressure: the share of the last five minutes (and of the last minute) in which work on this node waited for a CPU. Load: the 1, 5 and 15 minute load averages."
                  >
                    {node.cpu_pressure_60 != null ? `${node.cpu_pressure_60.toFixed(1)}% last min · ` : ""}
                    {node.cpu_count} cpu
                  </div>
                )}
                {node && (
                  <div className="mt-1 font-mono text-[11.5px] text-faint">
                    load {node.load1.toFixed(2)} · {node.load5.toFixed(2)} · {node.load15.toFixed(2)}
                    {node.load_is_hosts ? " (the container host's, not this node's)" : ""}
                  </div>
                )}
                {node && <div className="mt-2 font-mono text-[11.5px] text-faint">up {uptime(node.uptime_seconds)}</div>}
              </div>
            </div>
          </StateView>
        </Section>

        <Section
          title="Cluster"
          actions={
            <Link to="/modules/cluster" className="text-[12px] text-accent hover:underline">
              Details
            </Link>
          }
        >
          <StateView
            loading={cluster.isLoading}
            error={cluster.isError ? cluster.error : undefined}
            errorContext="cluster membership"
            empty={!cluster.isLoading && !cluster.isError && nodes.length === 0}
            emptyMessage="No cluster members registered."
            skeleton={<div className="h-24 animate-pulse rounded-md bg-surface2" />}
          >
            <div className="space-y-2">
              {nodes.map((n) => (
                <div
                  key={n.name}
                  className="flex items-center justify-between gap-3 rounded-[9px] border border-line bg-bg2 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[13.5px] font-semibold">{n.name}</span>
                      {n.role && <StatusPill tone="accent">{n.role}</StatusPill>}
                      {n.self && <StatusPill tone="neutral">this node</StatusPill>}
                    </div>
                    {n.fqdn && <div className="mt-0.5 truncate font-mono text-[11px] text-faint">{n.fqdn}</div>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {n.services_total !== undefined && (
                      <span className="font-mono text-[11px] text-muted tabular">
                        {n.services_healthy}/{n.services_total}
                      </span>
                    )}
                    <StatusPill tone={n.health === "healthy" ? "ok" : n.health === "critical" ? "bad" : "warn"}>
                      {n.health ?? "unknown"}
                    </StatusPill>
                  </div>
                </div>
              ))}
            </div>
          </StateView>
        </Section>
      </div>

      <Section
        className="mt-4"
        title="Storage"
        actions={
          <Link to="/modules/juicefs" className="text-[12px] text-accent hover:underline">
            Details
          </Link>
        }
      >
        <StateView
          loading={capacity.isLoading}
          error={capacity.isError ? capacity.error : undefined}
          errorContext="storage capacity"
          skeleton={<div className="h-24 animate-pulse rounded-md bg-surface2" />}
        >
          {capacity.data && <StoragePanel c={capacity.data} />}
        </StateView>
      </Section>

      {attention > 0 && (
        <Section
          className="mt-4"
          title="Needs attention"
          actions={
            <Link to="/modules" className="text-[12px] text-accent hover:underline">
              All modules
            </Link>
          }
          bodyClassName="p-0"
        >
          <div className="divide-y divide-line">
            {(modules.data?.items ?? [])
              .filter((m) => m.health === "degraded" || m.health === "critical")
              .sort((a) => (a.health === "critical" ? -1 : 1))
              .map((m) => (
                <Link
                  key={m.manifest.name}
                  to={`/modules/${m.manifest.name}`}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-surface2"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[13.5px] font-medium">{m.manifest.title}</div>
                    <div className="truncate text-[12px] text-muted">{m.manifest.description}</div>
                  </div>
                  <StatusPill tone={m.health === "critical" ? "bad" : "warn"}>{m.health}</StatusPill>
                </Link>
              ))}
          </div>
        </Section>
      )}

      {/* The node's OWN health, as a terminal would print it.
          It was keyed "ozpanel" in the per-module Overview map, and no module is called ozpanel —
          so it was written and never rendered. It never belonged there: it reads
          /ozpanel/health/terminal, which is this NODE's health across every module, not one
          module's. The dashboard is where a whole-node answer belongs. */}
      <HealthTerminal />
    </div>
  );
}

function Resource({ label, pct, sub }: { label: string; pct?: number; sub?: string | false }) {
  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <Eyebrow>{label}</Eyebrow>
        <span className="text-[19px] font-[660] tracking-[-0.01em] tabular">
          {pct !== undefined ? `${Math.round(pct)}%` : "—"}
        </span>
      </div>
      <Meter pct={pct ?? 0} tone={pctTone(pct)} />
      {sub && <div className="mt-2 font-mono text-[11.5px] text-muted">{sub}</div>}
    </div>
  );
}

// StoragePanel — the shared storage tenants' sites, mail and apps live on, and each node's disk under it.
function StoragePanel({ c }: { c: Capacity }) {
  const limiting = c.nodes.find((n) => n.node === c.limited_by);
  const limitingPct = limiting && limiting.disk_total_bytes > 0
    ? ((limiting.disk_total_bytes - limiting.disk_available_bytes) / limiting.disk_total_bytes) * 100
    : undefined;
  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_1.2fr]">
      <div>
        <div className="flex items-center gap-2">
          <HardDrive size={15} className="text-muted" />
          <Eyebrow>Shared storage available</Eyebrow>
        </div>
        <div className="mt-1.5 text-[26px] font-[660] tracking-[-0.01em] tabular">
          {c.available_bytes !== undefined ? formatBytes(c.available_bytes) : "unknown"}
        </div>
        <div className="mt-1 text-[12.5px] text-muted">
          {c.used_bytes !== undefined ? `${formatBytes(c.used_bytes)} stored` : "stored: not measured"}
          {c.volumes.length > 0 && (
            <span className="text-faint">
              {" "}
              ({c.volumes.map((v) => `${v.volume} ${v.used_bytes !== undefined ? formatBytes(v.used_bytes) : "?"}`).join(" · ")})
            </span>
          )}
        </div>
        {limiting && limitingPct !== undefined && (
          <div className="mt-3">
            <Meter pct={limitingPct} tone={pctTone(limitingPct)} />
            <div className="mt-1.5 font-mono text-[11.5px] text-muted">
              limited by {limiting.node}: {Math.round(limitingPct)}% of its disk used
            </div>
          </div>
        )}
        <p className="mt-3 text-[12px] leading-relaxed text-muted">{c.detail}</p>
      </div>
      <div className="space-y-2">
        {c.nodes.map((n) => {
          const pct = n.disk_total_bytes > 0 ? ((n.disk_total_bytes - n.disk_available_bytes) / n.disk_total_bytes) * 100 : undefined;
          return (
            <div key={n.node} className="rounded-[9px] border border-line bg-bg2 px-3 py-2.5">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[13.5px] font-semibold">{n.node}</span>
                  {n.node === c.limited_by && <StatusPill tone="warn">limits shared storage</StatusPill>}
                  {!n.up && <StatusPill tone="bad">down</StatusPill>}
                </div>
                <span className="font-mono text-[11.5px] text-muted tabular">
                  {n.disk_total_bytes > 0 ? `${formatBytes(n.disk_available_bytes)} free of ${formatBytes(n.disk_total_bytes)}` : "not reported"}
                </span>
              </div>
              <Meter pct={pct ?? 0} tone={pctTone(pct)} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
